// 권위: docs/ARCH_DASHBOARD.md §4.8 (화면 8 필터 사양) + §6.2 (REST 쿼리 파라미터)
// 화면 상태 ↔ URL 쿼리스트링 동기화용 타입.

export type Severity = 'FATAL' | 'ERROR' | 'WARN' | 'INFO';

export const ALL_SEVERITIES: Severity[] = ['FATAL', 'ERROR', 'WARN', 'INFO'];

// docs/ARCH_DASHBOARD.md §4.8 "source_agent 드롭다운" 목록 (동일 순서).
export const SOURCE_AGENTS: string[] = [
  'governance',
  'leading',
  'trading',
  'short',
  'llm_a',
  'llm_b',
  'llm_c',
  'llm_d',
  'llm_e',
  'data',
  'broker',
  'obs',
  'dashboard',
  'batch',
];

export const PAGE_SIZES: number[] = [50, 100, 200];

export interface SystemLogFilters {
  severities: Severity[];           // 복수 선택, 비어있으면 전체.
  sources: string[];                 // 복수 선택, 비어있으면 전체.
  eventCode: string;                 // glob 패턴 (* 허용) — 백엔드에서 SQL LIKE % 로 치환.
  from: string;                      // datetime-local 입력 값 ("" 이면 미설정).
  to: string;
  q: string;                         // summary full-text 검색.
  unackedOnly: boolean;
  page: number;                      // 0-origin.
  pageSize: number;
}

export const DEFAULT_FILTERS: SystemLogFilters = {
  severities: [],
  sources: [],
  eventCode: '',
  from: '',
  to: '',
  q: '',
  unackedOnly: false,
  page: 0,
  pageSize: 50,
};

/**
 * 필터 → URL 쿼리스트링 직렬화.
 * - 기본값과 동일한 항목은 URL 에 쓰지 않는다 (북마크 단순화).
 * - datetime-local 값은 그대로(초 미포함) 기록하고, API 전송 시 `:00Z` 를 붙여 ISO8601 화.
 */
export function filtersToSearchParams(f: SystemLogFilters): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.severities.length > 0) sp.set('severity', f.severities.join(','));
  if (f.sources.length > 0) sp.set('source', f.sources.join(','));
  if (f.eventCode.trim()) sp.set('event_code', f.eventCode.trim());
  if (f.from) sp.set('from', f.from);
  if (f.to) sp.set('to', f.to);
  if (f.q.trim()) sp.set('q', f.q.trim());
  if (f.unackedOnly) sp.set('unacked', '1');
  if (f.page > 0) sp.set('page', String(f.page));
  if (f.pageSize !== DEFAULT_FILTERS.pageSize) sp.set('page_size', String(f.pageSize));
  return sp;
}

export function filtersFromSearchParams(sp: URLSearchParams): SystemLogFilters {
  const sev = (sp.get('severity') ?? '')
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter((s): s is Severity => (ALL_SEVERITIES as string[]).includes(s));

  const src = (sp.get('source') ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const pageSize = parseInt(sp.get('page_size') ?? '', 10);
  const page = parseInt(sp.get('page') ?? '', 10);

  return {
    severities: sev,
    sources: src,
    eventCode: sp.get('event_code') ?? '',
    from: sp.get('from') ?? '',
    to: sp.get('to') ?? '',
    q: sp.get('q') ?? '',
    unackedOnly: sp.get('unacked') === '1' || sp.get('unacked') === 'true',
    page: Number.isFinite(page) && page > 0 ? page : 0,
    pageSize: PAGE_SIZES.includes(pageSize) ? pageSize : DEFAULT_FILTERS.pageSize,
  };
}

/**
 * 필터 → `listEvents` 호출용 쿼리 객체.
 * datetime-local("YYYY-MM-DDTHH:mm") 은 로컬 시각이지만 백엔드는 ISO8601 파싱만 하므로 Z 를 붙여 UTC 가정 전송.
 * (완벽한 TZ 처리는 후속 과제 — TODO).
 */
export function filtersToQuery(f: SystemLogFilters) {
  return {
    severity: f.severities.length > 0 ? f.severities.join(',') : undefined,
    source: f.sources.length > 0 ? f.sources.join(',') : undefined,
    eventCode: f.eventCode.trim() || undefined,
    from: f.from ? toIso(f.from) : undefined,
    to: f.to ? toIso(f.to) : undefined,
    q: f.q.trim() || undefined,
    unacked: f.unackedOnly ? true : undefined,
    page: f.page,
    pageSize: f.pageSize,
  };
}

function toIso(dtLocal: string): string {
  // "2026-04-20T09:00" → "2026-04-20T09:00:00Z"
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dtLocal)) return `${dtLocal}:00Z`;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(dtLocal)) return `${dtLocal}Z`;
  return dtLocal;
}
